import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Label,
  Pagination,
  Popup,
  Select,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';

const DIRECTION_OPTIONS = [
  { key: 'all', text: '全部方向', value: '' },
  { key: 'in', text: '入站（电子签平台 → 网关）', value: 'in' },
  { key: 'out', text: '出站（网关 → 业务方）', value: 'out' },
];

const STATUS_OPTIONS = [
  { key: 'all', text: '全部状态', value: '' },
  { key: '0', text: '待重试', value: '0' },
  { key: '1', text: '成功', value: '1' },
  { key: '2', text: '失败', value: '2' },
];

const statusLabel = (status) => {
  if (status === 1) return { text: '成功', color: 'green' };
  if (status === 2) return { text: '失败', color: 'red' };
  return { text: '待重试', color: 'orange' };
};

const ContractNotifyTable = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');

  const loadLogs = async (page = 1, dir = direction, st = status) => {
    setLoading(true);
    let url = `/api/contract/notify/?p=${page - 1}`;
    if (dir) {
      url += `&direction=${dir}`;
    }
    if (st !== '') {
      url += `&status=${st}`;
    }
    const res = await API.get(url);
    const { success, message, data, total: count } = res.data;
    if (success) {
      setLogs(data || []);
      setTotal(count || 0);
      setActivePage(page);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLogs(1).catch((reason) => showError(reason));
  }, []);

  const retry = async (log) => {
    const res = await API.post(`/api/contract/notify/${log.id}/retry`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '已触发重试');
      setTimeout(() => {
        loadLogs(activePage).catch((reason) => showError(reason));
      }, 1500);
    } else {
      showError(message);
    }
  };

  return (
    <>
      <Form>
        <Form.Group>
          <Form.Field width={5}>
            <Select
              options={DIRECTION_OPTIONS}
              value={direction}
              onChange={(e, { value }) => {
                setDirection(value);
                loadLogs(1, value, status).catch((reason) => showError(reason));
              }}
            />
          </Form.Field>
          <Form.Field width={5}>
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={(e, { value }) => {
                setStatus(value);
                loadLogs(1, direction, value).catch((reason) => showError(reason));
              }}
            />
          </Form.Field>
          <Button
            type='button'
            loading={loading}
            onClick={() => loadLogs(1).catch((reason) => showError(reason))}
          >
            刷新
          </Button>
        </Form.Group>
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>任务</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>方向</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>事件</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>签署方</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>目标地址</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>HTTP</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>尝试</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>状态</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>内容</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>时间</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {logs.map((log) => {
            const label = statusLabel(log.status);
            return (
              <Table.Row key={log.id}>
                <Table.Cell>{log.id}</Table.Cell>
                <Table.Cell>
                  <div>
                    <code style={{ fontSize: '12px' }}>{log.task_no || '-'}</code>
                  </div>
                  <div style={{ color: '#8a919f', fontSize: '12px' }}>#{log.task_id}</div>
                </Table.Cell>
                <Table.Cell>{log.direction === 'out' ? '出站' : '入站'}</Table.Cell>
                <Table.Cell>{log.event}</Table.Cell>
                <Table.Cell>
                  <code style={{ fontSize: '12px' }}>{log.signer_union_id || '-'}</code>
                </Table.Cell>
                <Table.Cell>
                  <code style={{ fontSize: '12px' }}>{log.target_url || '-'}</code>
                </Table.Cell>
                <Table.Cell>{log.http_status || '-'}</Table.Cell>
                <Table.Cell>{log.attempt}</Table.Cell>
                <Table.Cell>
                  <Label basic color={label.color}>
                    {label.text}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  <Popup
                    wide
                    hoverable
                    trigger={<span style={{ fontSize: '12px' }}>查看</span>}
                    content={
                      <pre style={{ maxWidth: 520, whiteSpace: 'pre-wrap' }}>
                        {log.raw_body || '-'}
                        {'\n\n响应：'}
                        {log.response_body || '-'}
                        {log.last_error ? `\n\n错误：${log.last_error}` : ''}
                      </pre>
                    }
                  />
                </Table.Cell>
                <Table.Cell>
                  {log.created_time > 0 ? timestamp2string(log.created_time) : '-'}
                </Table.Cell>
                <Table.Cell>
                  {log.direction === 'out' && log.status !== 1 ? (
                    <Button size='mini' basic onClick={() => retry(log)}>
                      重试
                    </Button>
                  ) : (
                    '-'
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='12'>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={(e, { activePage: page }) =>
                  loadLogs(page).catch((reason) => showError(reason))
                }
                size='small'
                siblingRange={1}
                totalPages={Math.max(1, Math.ceil(total / ITEMS_PER_PAGE))}
              />
              <span style={{ color: '#8a919f' }}>共 {total} 条回调记录</span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default ContractNotifyTable;
